import InboxClient from 'src/clients/InboxClient';
import { NOTIFICATIONS_STATUSES } from 'src/constants';
import { InboxNotificationDetailsResponse } from 'src/entities/Notifications';

import { ApiMethods, ApiTags, appAPI } from '.';

export interface GetNotificationDetailsBody {
  id: string;
}

export interface UpdateNotificationReadStatusBody {
  isRead: boolean;
}
export interface UpdateNotificationStatusBody {
  notificationId: string;
  status: string;
}

export interface UpdateNotificationBody
  extends UpdateNotificationReadStatusBody {
  id: string;
}

export interface DeleteNotificationBody {
  id: string;
}

export enum DeleteAllNotificationsType {
  ALL = 'ALL',
  READ = 'READ',
  UNREAD = 'UNREAD',
}
export interface DeleteAllNotificationsBody {
  type: DeleteAllNotificationsType;
}

export const SIDEBAR_PAGES_TO_LOAD = 1;

/**
 * This function handles updating the notification status in the rtk query cache.
 * @param notificationsDraft - the current notifications draft (cache)
 * @param notificationId - the notification id to update
 * @param status - the status to update the notification to
 * @returns the updated notifications draft
 */
export const updateNotificationStatus = (
  notificationsDraft: InboxNotificationDetailsResponse[],
  notificationId: string,
  isRead: boolean,
) =>
  notificationsDraft.map((notification) => {
    // lookup the notification to update and update its data attribute since
    // it is used to determine the notification status. We will replace this
    // with the isRead property.
    if (notification.id === notificationId) {
      notification.isRead = isRead;
    }
    return notification;
  });

export const inboxApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    getInboxNotifications: build.query<
      InboxNotificationDetailsResponse[],
      number | void
    >({
      queryFn: async (pagesToLoad) => {
        const resolvedPagesToLoad =
          typeof pagesToLoad === 'number' ? pagesToLoad : Infinity;
        const notifications = await InboxClient.loadPaginatedNotifications(
          [],
          resolvedPagesToLoad,
        );
        return { data: notifications };
      },
      providesTags: [ApiTags.inbox_notifications],
    }),
    getInboxNotificationDetails: build.query<
      InboxNotificationDetailsResponse,
      GetNotificationDetailsBody
    >({
      query: ({ id }) => ({
        path: `/notifications/${id}/details`,
        method: ApiMethods.get,
        options: {},
      }),
    }),
    updateInboxNotificationStatus: build.mutation<
      any,
      UpdateNotificationStatusBody
    >({
      query: ({ notificationId, status }) => ({
        path: `/notifications?groupID=${notificationId}`,
        method: ApiMethods.put,
        options: {
          body: {
            status,
          },
        },
      }),
      async onQueryStarted(
        { status, notificationId: updatedNotificationId },
        { dispatch, queryFulfilled },
      ) {
        // optimistically update notification status to read.
        const patchResults = [undefined, SIDEBAR_PAGES_TO_LOAD].map(
          (pagesToLoad) =>
            dispatch(
              inboxApi.util.updateQueryData(
                'getInboxNotifications',
                pagesToLoad,
                (notificationsDraft) => {
                  updateNotificationStatus(
                    notificationsDraft,
                    updatedNotificationId,
                    status === NOTIFICATIONS_STATUSES.READ,
                  );
                },
              ),
            ),
        );
        try {
          await queryFulfilled;
        } catch (error) {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
    }),
    deleteAllNotifications: build.mutation<any, DeleteAllNotificationsBody>({
      query: ({ type }) => ({
        path: `/notifications?type=${type}`,
        method: ApiMethods.del,
        options: {},
      }),
      async onQueryStarted({ type }, { dispatch, queryFulfilled }) {
        // optimistically remove the deleted notifications from list
        const delResults = [undefined, SIDEBAR_PAGES_TO_LOAD].map(
          (pagesToLoad) =>
            dispatch(
              inboxApi.util.updateQueryData(
                'getInboxNotifications',
                pagesToLoad,
                (draft) => {
                  if (type === DeleteAllNotificationsType.ALL) {
                    draft.splice(0);
                  } else if (type === DeleteAllNotificationsType.READ) {
                    const newDraft = draft.filter((d) => !d.isRead);
                    draft.splice(0, draft.length, ...newDraft);
                  }
                },
              ),
            ),
        );
        try {
          await queryFulfilled;
        } catch (error) {
          delResults.forEach((delResult) => delResult.undo());
        }
      },
    }),
    deleteNotification: build.mutation<any, DeleteNotificationBody>({
      query: ({ id }) => ({
        path: `/notifications/${encodeURIComponent(id)}`,
        method: ApiMethods.del,
        options: {},
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        // optimistically remove notification from list
        const delResults = [undefined, SIDEBAR_PAGES_TO_LOAD].map(
          (pagesToLoad) =>
            dispatch(
              inboxApi.util.updateQueryData(
                'getInboxNotifications',
                pagesToLoad,
                (draft) => {
                  const index = draft.findIndex((n) => n.id === id);
                  if (index !== -1) {
                    draft.splice(index, 1);
                  }
                },
              ),
            ),
        );
        try {
          await queryFulfilled;
        } catch (error) {
          delResults.forEach((delResult) => delResult.undo());
        }
      },
    }),
    updateNotification: build.mutation<any, UpdateNotificationBody>({
      query: ({ id, isRead }) => ({
        path: `/notifications/${encodeURIComponent(id)}?isRead=${isRead}`,
        method: ApiMethods.put,
        options: {},
      }),
      async onQueryStarted({ isRead, id }, { dispatch, queryFulfilled }) {
        // optimistically update notification status
        const patchResults = [undefined, SIDEBAR_PAGES_TO_LOAD].map(
          (pagesToLoad) =>
            dispatch(
              inboxApi.util.updateQueryData(
                'getInboxNotifications',
                pagesToLoad,
                (notificationsDraft) => {
                  updateNotificationStatus(notificationsDraft, id, isRead);
                },
              ),
            ),
        );
        try {
          await queryFulfilled;
        } catch (error) {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
    }),
    updateAllNotifications: build.mutation<
      any,
      UpdateNotificationReadStatusBody
    >({
      query: ({ isRead }) => ({
        path: `/notifications/bulk-update?isRead=${isRead}`,
        method: ApiMethods.put,
        options: {},
      }),
      async onQueryStarted({ isRead }, { dispatch, queryFulfilled }) {
        // optimistically update all notifications from list
        const updateResults = [undefined, SIDEBAR_PAGES_TO_LOAD].map(
          (pagesToLoad) =>
            dispatch(
              inboxApi.util.updateQueryData(
                'getInboxNotifications',
                pagesToLoad,
                (draft) => {
                  draft.map((notification) => {
                    notification.isRead = isRead;
                    return notification;
                  });
                },
              ),
            ),
        );
        try {
          await queryFulfilled;
        } catch (error) {
          updateResults.forEach((updateResult) => updateResult.undo());
        }
      },
    }),
  }),
});

export const {
  useGetInboxNotificationsQuery,
  useUpdateInboxNotificationStatusMutation,
  useGetInboxNotificationDetailsQuery,
  useDeleteAllNotificationsMutation,
  useDeleteNotificationMutation,
  useUpdateNotificationMutation,
  useUpdateAllNotificationsMutation,
} = inboxApi;
